import React, { useMemo, useState, useEffect } from 'react';
import { useSalesReport } from '../../hooks/useSalesReport';
import { useInventory } from '../../hooks/useInventory';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, cn } from '../../lib/utils';
import DashboardHeader from './DashboardHeader';
import DatePresetButtons, { getPresetRange, DEFAULT_PRESET } from '../common/DatePresetButtons';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, RefreshCw, Settings, Info, MapPin } from 'lucide-react';
import { startOfMonth, endOfMonth, format, startOfToday, subDays, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
registerLocale('es', es);
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';

export default function ProductAnalyticsScreen() {
    const navigate = useNavigate();

    // --- State ---
    const [datePreset, setDatePreset] = useState(DEFAULT_PRESET);
    const [dateRange, setDateRange] = useState(() => getPresetRange(DEFAULT_PRESET));
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('performance'); // 'stars', 'low_rotation', 'zero_inventory', 'performance'
    const [selectedBranch, setSelectedBranch] = useState('all');

    // Filters
    const [filterStatus, setFilterStatus] = useState('ALL'); // ALL, ACTIVE, ARCHIVED
    const [filterInventory, setFilterInventory] = useState('ALL'); // ALL, WITH_STOCK, NO_STOCK

    // Sort
    const [sortOption, setSortOption] = useState('sales_desc'); // sales_desc, sales_asc, stock_desc, stock_asc, days_inv_desc, days_no_sale_desc

    // Hooks
    const { report, loading: reportLoading, getSalesReport, clearReport, error } = useSalesReport();
    const { inventory, loading: inventoryLoading, fetchInventory, clearInventoryCache } = useInventory();

    // Debug: Log inventory state
    useEffect(() => {
        console.log('[ProductAnalytics] Inventory state:', {
            loaded: inventory.length > 0,
            count: inventory.length,
            loading: inventoryLoading,
            sample: inventory.length > 0 ? inventory[0] : null
        });
    }, [inventory, inventoryLoading]);

    // --- Effects ---
    useEffect(() => {
        if (dateRange.from && dateRange.to) {
            const start = format(dateRange.from, 'yyyy-MM-dd');
            const end = format(dateRange.to, 'yyyy-MM-dd');
            getSalesReport(start, end);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange.from, dateRange.to]); // Only re-run when dates change, not when getSalesReport changes

    // NOTE: Inventory fetch is OPTIONAL and disabled by default to avoid timeout errors
    // The screen can work perfectly with sales data only
    // Uncomment the following to enable inventory integration:
    //
    // useEffect(() => {
    //     fetchInventory();
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);
    //
    // For now, ProductAnalyticsScreen will build product info from sales data only

    // --- Branch Management ---
    const availableBranches = useMemo(() => {
        if (!report || !report.orders) return [];
        const branches = new Set();
        report.orders.forEach(order => {
            if (order.branch) branches.add(order.branch);
        });
        return Array.from(branches).sort();
    }, [report]);

    const filteredOrders = useMemo(() => {
        if (!report || !report.orders) return [];
        if (selectedBranch === 'all') return report.orders;
        return report.orders.filter(order => order.branch === selectedBranch);
    }, [report, selectedBranch]);

    // --- Logic ---

    const mergedData = useMemo(() => {
        const salesMap = new Map();
        const productInfoMap = new Map(); // Store product names and brands from sales

        // 1. Aggregating Sales
        if (filteredOrders && filteredOrders.length > 0) {
            console.log(`[ProductAnalytics] Processing ${filteredOrders.length} orders`);

            let itemsProcessed = 0;
            filteredOrders.forEach((o, orderIdx) => {
                const items = o.items || o.products || [];
                const orderDate = new Date(o.date || o.creationDate);

                items.forEach((i, itemIdx) => {
                    const id = String(i.id);
                    itemsProcessed++;

                    // Debug: Log first 3 items to verify structure
                    if (itemsProcessed <= 3) {
                        console.log(`[ProductAnalytics] Item #${itemsProcessed}:`, {
                            id: i.id,
                            name: i.name,
                            brandName: i.brandName,
                            categoryName: i.categoryName,
                            sku: i.sku,
                            quantity: i.quantity,
                            totalSales: i.totalSales,
                            price: i.price
                        });
                    }

                    // Store product info from first occurrence
                    if (!productInfoMap.has(id)) {
                        const categoryValue = i.categoryName || i.category || i.linea || 'General';
                        // DynamiaERP sales don't include brand, so we use category as temporary fallback
                        // This will be replaced with real brand from inventory when available
                        const brandValue = (i.brandName && i.brandName !== 'Sin Marca') ? i.brandName : categoryValue;
                        productInfoMap.set(id, {
                            name: i.name || 'Producto sin nombre',
                            brand: brandValue,
                            category: categoryValue,
                            sku: i.sku || ''
                        });

                        // Debug: Log the first product info stored
                        if (productInfoMap.size <= 3) {
                            console.log(`[ProductAnalytics] Stored product info for ID ${id}:`, {
                                name: i.name,
                                brand: brandValue,
                                category: categoryValue,
                                sku: i.sku
                            });
                        }
                    }

                    if (!salesMap.has(id)) {
                        salesMap.set(id, {
                            id,
                            sales: 0,
                            revenue: 0,
                            orders: 0,
                            lastSaleDate: null
                        });
                    }
                    const entry = salesMap.get(id);
                    entry.sales += (Number(i.quantity) || 0);
                    entry.revenue += (Number(i.totalSales) || 0);
                    entry.orders += 1;
                    if (!entry.lastSaleDate || orderDate > entry.lastSaleDate) {
                        entry.lastSaleDate = orderDate;
                    }
                });
            });

            console.log(`[ProductAnalytics] Processed ${itemsProcessed} total items from ${filteredOrders.length} orders`);
            console.log(`[ProductAnalytics] Unique products found: ${productInfoMap.size}`);
        }

        // 2. Merging with Inventory
        // ALWAYS use sales data as primary source, enrich with inventory if available
        // This ensures we show products that actually have sales
        //
        // IMPORTANT: Sales IDs don't match inventory IDs, so we need multiple matching strategies:
        // 1. Match by ID (direct)
        // 2. Match by SKU/barcode (most reliable for DynamiaERP)
        // 3. Match by product name (fallback)

        const inventoryMapById = new Map();
        const inventoryMapBySku = new Map();
        const inventoryMapByName = new Map();

        inventory.forEach(inv => {
            inventoryMapById.set(String(inv.id), inv);
            if (inv.sku) {
                inventoryMapBySku.set(inv.sku.toLowerCase().trim(), inv);
            }
            if (inv.name) {
                inventoryMapByName.set(inv.name.toLowerCase().trim(), inv);
            }
        });

        console.log(`[ProductAnalytics] Built inventory indexes: ${inventoryMapById.size} by ID, ${inventoryMapBySku.size} by SKU, ${inventoryMapByName.size} by name`);

        const mergedProducts = Array.from(salesMap.keys()).map(productId => {
            const s = salesMap.get(productId);
            const info = productInfoMap.get(productId) || { name: 'Producto Desconocido', brand: 'Desconocido', category: 'General', sku: '' };

            // Try multiple matching strategies to find inventory data
            let inv = inventoryMapById.get(productId); // Try by ID first

            if (!inv && info.sku) {
                // Try by SKU (most reliable for DynamiaERP)
                inv = inventoryMapBySku.get(info.sku.toLowerCase().trim());
            }

            if (!inv && info.name) {
                // Try by name as fallback
                inv = inventoryMapByName.get(info.name.toLowerCase().trim());
            }

            // Calculate days without sales
            const now = new Date();
            const lastSale = s.lastSaleDate ? new Date(s.lastSaleDate) : null;
            const daysNoSale = lastSale ? Math.floor((now - lastSale) / (1000 * 60 * 60 * 24)) : 9999;

            return {
                id: productId,
                // Use inventory data if available, otherwise use sales data
                name: inv?.name || info.name,
                brand: inv?.brand || info.brand, // Prefer inventory brand (real brand) over sales category fallback
                category: inv?.category || info.category, // Prefer inventory category
                sku: inv?.sku || info.sku,
                stock: inv?.stock || 0,
                active: inv?.active !== undefined ? inv.active : true,
                // Sales data (always from sales)
                salesUnits: s.sales,
                salesRevenue: s.revenue,
                ordersCount: s.orders,
                daysNoSale: daysNoSale,
                contribution: 0, // Will calculate total later
                // Debug info
                matchedBy: inv ? (inventoryMapById.get(productId) ? 'id' : (inventoryMapBySku.get(info.sku?.toLowerCase().trim()) ? 'sku' : 'name')) : 'none'
            };
        });

        console.log(`[ProductAnalytics] Final merged products: ${mergedProducts.length}`);

        // Log matching statistics
        const matchStats = mergedProducts.reduce((acc, p) => {
            acc[p.matchedBy] = (acc[p.matchedBy] || 0) + 1;
            return acc;
        }, {});
        console.log(`[ProductAnalytics] Inventory matching stats:`, matchStats);

        if (mergedProducts.length > 0) {
            console.log(`[ProductAnalytics] Sample merged product:`, {
                id: mergedProducts[0].id,
                name: mergedProducts[0].name,
                brand: mergedProducts[0].brand,
                category: mergedProducts[0].category,
                sku: mergedProducts[0].sku,
                stock: mergedProducts[0].stock,
                salesUnits: mergedProducts[0].salesUnits,
                salesRevenue: mergedProducts[0].salesRevenue,
                matchedBy: mergedProducts[0].matchedBy
            });
            console.log(`[ProductAnalytics] First 3 products (brand/category):`,
                mergedProducts.slice(0, 3).map(p => ({ id: p.id, name: p.name, brand: p.brand, category: p.category, matchedBy: p.matchedBy }))
            );
        }

        return mergedProducts;
    }, [filteredOrders, inventory]);

    // 3. Totals & Contribution
    const finalData = useMemo(() => {
        const totalRev = mergedData.reduce((acc, p) => acc + p.salesRevenue, 0);
        return mergedData.map(p => ({
            ...p,
            contribution: totalRev > 0 ? (p.salesRevenue / totalRev) * 100 : 0
        }));
    }, [mergedData]);


    // 4. Filtering & Sorting
    const processedData = useMemo(() => {
        let data = finalData;

        // Search
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            data = data.filter(p => p.name?.toLowerCase().includes(lower) || p.sku?.toLowerCase().includes(lower));
        }

        // Filters
        if (filterStatus === 'ACTIVE') data = data.filter(p => p.active);
        if (filterStatus === 'ARCHIVED') data = data.filter(p => !p.active);

        if (filterInventory === 'WITH_STOCK') data = data.filter(p => p.stock > 0);
        if (filterInventory === 'NO_STOCK') data = data.filter(p => p.stock <= 0);

        // Tabs Logic
        if (activeTab === 'stars') {
            // Top 10 by contribution
            data = [...data].sort((a, b) => b.salesRevenue - a.salesRevenue).slice(0, 10);
        } else if (activeTab === 'low_rotation') {
            // Products with more than 30 days without sales
            // If inventory is not loaded, just show products with high daysNoSale
            data = data.filter(p => p.daysNoSale > 30);
        } else if (activeTab === 'zero_inventory') {
            // When inventory is loaded: show products with stock <= 0
            // When inventory is NOT loaded: show products with the lowest sales
            if (inventory.length > 0) {
                data = data.filter(p => p.stock <= 0);
            } else {
                // Show products with lowest sales (bottom 20%)
                const sorted = [...data].sort((a, b) => a.salesRevenue - b.salesRevenue);
                const cutoff = Math.ceil(sorted.length * 0.2);
                data = sorted.slice(0, cutoff);
            }
        } else {
            // Performance / All
            // Apply sorting
            if (sortOption === 'sales_desc') data.sort((a, b) => b.salesUnits - a.salesUnits);
            if (sortOption === 'sales_asc') data.sort((a, b) => a.salesUnits - b.salesUnits);
            if (sortOption === 'stock_desc') data.sort((a, b) => b.stock - a.stock);
            if (sortOption === 'stock_asc') data.sort((a, b) => a.stock - b.stock);
            if (sortOption === 'days_inv_desc') data.sort((a, b) => b.daysNoSale - a.daysNoSale); // Proxy for rotation
            if (sortOption === 'days_no_sale_desc') data.sort((a, b) => b.daysNoSale - a.daysNoSale);
        }

        return data;
    }, [finalData, searchTerm, filterStatus, filterInventory, activeTab, sortOption]);


    // Stats
    const stats = useMemo(() => {
        const totalRev = processedData.reduce((acc, p) => acc + p.salesRevenue, 0);
        const totalUnits = processedData.reduce((acc, p) => acc + p.salesUnits, 0);
        const totalOrders = report?.orders?.length || 1;

        return {
            revenue: totalRev,
            units: totalUnits,
            ticket: totalOrders > 0 ? totalRev / totalOrders : 0,
            count: processedData.length
        };
    }, [processedData, report]);


    // Handlers
    const handleGenerate = () => {
        const start = format(dateRange.from, 'yyyy-MM-dd');
        const end = format(dateRange.to, 'yyyy-MM-dd');
        getSalesReport(start, end);
        fetchInventory(true); // Force refresh
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white px-4 py-5 md:p-8 space-y-4 md:space-y-6">
            <DashboardHeader activeTab="products" title="Ventas por Producto" subtitle="Análisis detallado por producto, inventario y rotación" />

            {error && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-md flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <p className="text-sm font-medium">{error.toString()}</p>
                    <Button variant="ghost" size="sm" className="ml-auto h-6 text-red-400 hover:text-red-300" onClick={handleGenerate}>Reintentar</Button>
                </div>
            )}

            {/* Controls Bar */}
            <Card className="bg-white/5 border-white/10">
                <CardContent className="p-3 sm:p-4 flex flex-col xl:flex-row gap-3 justify-between xl:items-center">
                    {/* Date Picker Section */}
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 min-w-0">
                        <DatePresetButtons value={datePreset} onChange={(preset, range) => { setDatePreset(preset); setDateRange(range); }} />

                        <div className="flex items-center gap-2">
                        {/* Custom Date Picker */}
                        <div className="flex flex-1 sm:flex-none items-center gap-2 bg-black/20 rounded-md px-3 h-9 border border-white/10">
                            <DatePicker
                                selected={dateRange.from}
                                onChange={(dates) => {
                                    const [start, end] = dates;
                                    setDatePreset('custom'); setDateRange({ from: start, to: end });
                                }}
                                startDate={dateRange.from}
                                endDate={dateRange.to}
                                selectsRange
                                locale="es"
                                dateFormat="dd/MM/yyyy"
                                className="bg-transparent text-sm w-full sm:w-[180px] focus:outline-none text-white text-center"
                            />
                        </div>

                        <Button size="sm" onClick={handleGenerate} className="bg-blue-600 hover:bg-blue-500 text-white h-9 px-4">
                            Generar
                        </Button>

                        <Button size="icon" variant="ghost" onClick={clearReport} title="Limpiar Caché" className="h-9 w-9 text-white/50 hover:text-white">
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                        </div>
                    </div>

                    {/* Right Controls - Fixed UX */}
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Optional: Load Inventory */}
                        {inventory.length === 0 && !inventoryLoading && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fetchInventory(true)}
                                className="h-10 px-4 bg-white/5 border-white/10 text-white hover:bg-white/10 gap-2 transition-all font-medium"
                                title="Cargar datos de inventario (opcional)"
                            >
                                <Settings className="w-4 h-4" />
                                Cargar Inventario
                            </Button>
                        )}
                        {inventoryLoading && (
                            <span className="text-xs text-white/50 animate-pulse">Cargando...</span>
                        )}

                        {/* Config Sort Dialog */}
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" className="h-10 px-4 bg-white/5 border-white/10 text-white hover:bg-white/10 gap-2 transition-all font-medium">
                                    <Settings className="w-4 h-4" />
                                    Configurar<span className="hidden sm:inline"> ventas</span>
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-white/10 text-white">
                                <DialogHeader>
                                    <DialogTitle>Configuración de Vista</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                    <label className="text-sm font-medium text-white/70">Ordenar por</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { id: 'sales_desc', label: 'Más vendidos' },
                                            { id: 'sales_asc', label: 'Menos vendidos' },
                                            { id: 'stock_desc', label: 'Mayor inventario' },
                                            { id: 'stock_asc', label: 'Menor inventario' },
                                            { id: 'days_inv_desc', label: 'Menor rotación' },
                                            { id: 'days_no_sale_desc', label: 'Más días sin venta' }
                                        ].map(opt => (
                                            <Button
                                                key={opt.id}
                                                variant={sortOption === opt.id ? "default" : "outline"}
                                                onClick={() => setSortOption(opt.id)}
                                                className={`justify-start ${sortOption === opt.id ? 'bg-blue-600' : 'bg-transparent border-white/10'}`}
                                            >
                                                {opt.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </CardContent>

                {/* Branch Tabs Filter */}
                <div className="px-4 pb-4 border-t border-white/5 pt-4">
                    <div className="flex items-center gap-1 overflow-x-auto">
                        <div className="flex items-center px-2 text-slate-400 border-r border-white/10 mr-1 shrink-0">
                            <MapPin className="w-4 h-4 text-slate-400" />
                        </div>
                        <Button
                            variant={selectedBranch === 'all' ? 'default' : 'ghost'}
                            onClick={() => setSelectedBranch('all')}
                            className={cn(
                                "h-8 px-3 text-xs font-medium rounded-md transition-all shrink-0",
                                selectedBranch === 'all'
                                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                            )}
                        >
                            Todas
                        </Button>
                        {availableBranches.map(branch => (
                            <Button
                                key={branch}
                                variant={selectedBranch === branch ? 'default' : 'ghost'}
                                onClick={() => setSelectedBranch(branch)}
                                className={cn(
                                    "h-8 px-3 text-xs font-medium rounded-md whitespace-nowrap transition-all shrink-0",
                                    selectedBranch === branch
                                        ? "bg-blue-600 text-white shadow-sm shadow-blue-500/20"
                                        : "text-slate-400 hover:text-white hover:bg-white/5"
                                )}
                            >
                                {branch}
                            </Button>
                        ))}
                    </div>
                </div>
            </Card>

            {/* Filters Row */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex items-center gap-2 bg-white/5 p-1 rounded-lg border border-white/10">
                    <span className="text-xs font-semibold text-white/50 px-2 uppercase">Estado</span>
                    {['ALL', 'ACTIVE', 'ARCHIVED'].map(s => (
                        <button
                            key={s}
                            onClick={() => setFilterStatus(s)}
                            className={`px-3 py-1 text-xs rounded-md transition-all ${filterStatus === s ? 'bg-blue-600 text-white shadow' : 'text-white/60 hover:text-white'}`}
                        >
                            {s === 'ALL' ? 'Todos' : s === 'ACTIVE' ? 'Activos' : 'Archivados'}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 bg-white/5 p-1 rounded-lg border border-white/10">
                    <span className="text-xs font-semibold text-white/50 px-2 uppercase">Existencia</span>
                    {['ALL', 'WITH_STOCK', 'NO_STOCK'].map(s => (
                        <button
                            key={s}
                            onClick={() => setFilterInventory(s)}
                            className={`px-3 py-1 text-xs rounded-md transition-all ${filterInventory === s ? 'bg-purple-600 text-white shadow' : 'text-white/60 hover:text-white'}`}
                        >
                            {s === 'ALL' ? 'Todos' : s === 'WITH_STOCK' ? 'Con Stock' : 'Sin Stock'}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                    <Input
                        placeholder="Buscar producto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-white/5 border-white/10 text-white ring-offset-transparent focus-visible:ring-blue-500/50"
                    />
                </div>
            </div>

            {/* Info Banner - Inventory Optional */}
            {inventory.length === 0 && !inventoryLoading && (
                <div className="bg-blue-500/10 border border-blue-500/30 text-blue-300 px-4 py-3 rounded-md flex items-center gap-3">
                    <Info className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 text-sm">
                        <p className="font-medium">Mostrando datos de ventas únicamente (sin marcas ni stock reales)</p>
                        <p className="text-xs text-blue-200/70 mt-1">
                            Para ver marcas y stock reales desde el inventario, haz clic en el botón "Cargar Inventario" arriba.
                            El campo "Marca" muestra temporalmente la categoría del producto.
                        </p>
                    </div>
                </div>
            )}

            {/* Success Banner - Inventory Loaded */}
            {inventory.length > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 text-green-300 px-4 py-3 rounded-md flex items-center gap-3">
                    <Info className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 text-sm">
                        <p className="font-medium">✓ Inventario cargado: {inventory.length} productos</p>
                        <p className="text-xs text-green-200/70 mt-1">
                            Mostrando marcas y stock reales vinculados por SKU/nombre.
                        </p>
                    </div>
                </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatsCard title="Ventas netas (período)" value={formatCurrency(stats.revenue)} sub="" color="text-green-400" />
                <StatsCard title="Unidades vendidas" value={stats.units} sub="" color="text-blue-400" />
                <StatsCard title="Ticket Promedio" value={formatCurrency(stats.ticket)} sub="" color="text-yellow-400" />
                <StatsCard title="Productos Analizados" value={stats.count} sub={inventory.length === 0 ? "Solo datos de ventas" : ""} color="text-purple-400" />
            </div>

            {/* Tabs & Table */}
            <Card className="bg-transparent border-white/10">
                <CardHeader className="p-0 border-b border-white/10">
                    <div className="flex overflow-x-auto">
                        {[
                            { id: 'performance', label: 'Rendimiento por producto', desc: 'Comparativo de ventas e inventario' },
                            { id: 'stars', label: 'Productos Estrella', desc: 'Top 10 mayor contribución' },
                            { id: 'low_rotation', label: 'Menor Rotación', desc: '>30 días sin ventas' },
                            { id: 'zero_inventory', label: 'Inventario Cero', desc: 'Agotados sin ventas recientes' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                   flex flex-col items-start p-4 hover:bg-white/5 transition-colors border-b-2 min-w-[200px]
                                   ${activeTab === tab.id ? 'border-blue-500 bg-white/5' : 'border-transparent'}
                               `}
                            >
                                <span className={`text-sm font-semibold ${activeTab === tab.id ? 'text-blue-400' : 'text-white'}`}>{tab.label}</span>
                                <span className="text-xs text-white/50 mt-1 text-left">{tab.desc}</span>
                            </button>
                        ))}
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader className="bg-white/5 hover:bg-white/5">
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-white/70 w-[50px]">#</TableHead>
                                    <TableHead className="text-white/70">Producto</TableHead>
                                    <TableHead className="text-white/70">Marca</TableHead>
                                    <TableHead className="text-white/70">Línea</TableHead>
                                    <TableHead className="text-right text-white/70">Stock</TableHead>
                                    <TableHead className="text-right text-white/70">Unidades</TableHead>
                                    <TableHead className="text-right text-white/70">Ventas</TableHead>
                                    <TableHead className="text-right text-white/70">% Aporte</TableHead>
                                    <TableHead className="text-right text-white/70">Días sin Venta</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="text-white">
                                {reportLoading || inventoryLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="h-24 text-center">
                                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                                            <p className="text-xs text-white/50 mt-2">Cargando datos...</p>
                                        </TableCell>
                                    </TableRow>
                                ) : processedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="h-24 text-center text-white/50">
                                            No hay productos que coincidan con los filtros.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    processedData.map((p, idx) => (
                                        <TableRow key={p.id} className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-white/40 text-xs">{idx + 1}</TableCell>
                                            <TableCell className="font-medium">
                                                <div className="flex flex-col">
                                                    <span>{p.name}</span>
                                                    <span className="text-[10px] text-white/40 font-mono">{p.sku}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="border-white/10 bg-white/5 text-white/60 text-[10px] font-normal">
                                                    {p.brand}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="border-white/10 bg-purple-500/10 text-purple-300 text-[10px] font-normal">
                                                    {p.category}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-mono">
                                                <span className={p.stock <= 0 ? 'text-red-400' : 'text-blue-300'}>{p.stock}</span>
                                            </TableCell>
                                            <TableCell className="text-right font-mono">{p.salesUnits}</TableCell>
                                            <TableCell className="text-right font-mono text-green-400 font-bold">
                                                {formatCurrency(p.salesRevenue)}
                                            </TableCell>
                                            <TableCell className="text-right text-xs text-white/60">
                                                {p.contribution.toFixed(2)}%
                                            </TableCell>
                                            <TableCell className="text-right text-xs text-white/60">
                                                {p.daysNoSale > 900 ? '-' : `${p.daysNoSale} días`}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

        </div>
    );
}

function StatsCard({ title, value, sub, color }) {
    return (
        <Card className="bg-white/5 border-white/10 backdrop-blur">
            <CardContent className="p-4">
                <p className="text-xs text-white/50 uppercase mb-1">{title}</p>
                <div className={`text-2xl font-bold ${color}`}>{value}</div>
                {sub && <p className="text-[10px] text-white/40 mt-1">{sub}</p>}
            </CardContent>
        </Card>
    );
}
